/**
 * This hook is used to handle the drop event for the steps & operations in the test draft view.
 */

import { useState } from 'react';
import { api } from '~/trpc-extra/api';
import { z } from 'zod';

const zTestStepDropZoneData = z.object({
  prevStepId: z.string().optional(),
  nextStepId: z.string().optional(),
});

const zTestStepActionItemData = z.object({
  stepId: z.string(),
  type: z.literal('moveStep'),
});

export function useHandleMoveTestStep() {
  const trpcUtils = api.useUtils();
  const [isMoving, setIsMoving] = useState(false);
  const { mutateAsync: moveStep } = api.testStep.moveStep.useMutation();

  const handleMoveTestStep = async (actionItem: unknown, dropZone: unknown) => {
    const testStepDropZoneData = zTestStepDropZoneData.safeParse(dropZone);
    const testStepActionItemData = zTestStepActionItemData.safeParse(actionItem);

    if (testStepDropZoneData.success && testStepActionItemData.success) {
      const { prevStepId, nextStepId } = testStepDropZoneData.data;
      const { stepId: draggedStepId } = testStepActionItemData.data;
      const targetStepId = nextStepId || prevStepId;

      if (targetStepId && nextStepId !== draggedStepId && prevStepId !== draggedStepId) {
        try {
          setIsMoving(true);
          await moveStep({
            testStepId: draggedStepId,
            target: {
              type: nextStepId ? 'afterTestStep' : 'endOfStep',
              testStepId: targetStepId,
            },
          });

          await Promise.allSettled([
            trpcUtils.testContent.get.invalidate(),
            trpcUtils.testContent.getCode.invalidate(),
          ]);
        } catch (error) {
          console.error('Error moving test step', error);
        } finally {
          setIsMoving(false);
        }
      }
    } else {
      console.error('Invalid drop event', {
        source: actionItem,
        target: dropZone,
        sourceError: testStepActionItemData.error,
        targetError: testStepDropZoneData.error,
      });
    }
  };

  return { handleMoveTestStep, isMoving };
}
